import { CSSProperties, FC, useState } from "react";
import { Col, Row, Space, Tag, Typography, Button } from "antd";
import { ColumnType } from "antd/lib/table";
import { clsx } from "clsx";
import { GenericTable } from "../../../../common/GenericTable";
import { DynamicLeverEvaluation, OrderFormDynamicLever } from "../../../../types/OrderFormTypes";
import { SvgIcon } from "../../../../common/components/SvgIcon";
import { GenericCard } from "../../../../common/components/GenericCard";
import { GenericTabs } from "../../../../common/components/GenericTabs";
import { EvaluationTag } from "./EvaluationTag";
import classes from "./LerversTable.module.less";

interface LerversTableProps {
    levers?: OrderFormDynamicLever[];
    style?: CSSProperties;
}

enum TabKey {
    Optimizable = "Levers requiring attention",
    Optimized = "Levers in good standing",
}

export const LerversTable: FC<LerversTableProps> = ({ levers = [], style }) => {
    const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>([]);
    const [tab, setTab] = useState(TabKey.Optimizable);

    if (!levers?.length) {
        return (
            <GenericCard size="large" style={style}>
                <div className={classes.checkmark}>
                    <SvgIcon name="alert" size={32} color="#1890FF" />
                </div>
                <div className={classes.message}>Your levers are under evaluation.</div>
            </GenericCard>
        );
    }

    const optimized: OrderFormDynamicLever[] = [];
    const optimizable: OrderFormDynamicLever[] = [];

    for (const item of levers.sort((a, b) => Number(a.priority) - Number(b.priority))) {
        if (item.isActionable) {
            optimizable.push(item);
        } else {
            optimized.push(item);
        }
    }

    const generalColumns: ColumnType<OrderFormDynamicLever>[] = [
        {
            title: "Type",
            dataIndex: "type",
            key: "type",
            width: 130,
        },
        {
            title: "Included",
            dataIndex: "presentInContract",
            key: "presentInContract",
            width: 80,
            render: (value: boolean, row: OrderFormDynamicLever) => {
                if (value) {
                    return <SvgIcon size={14} name="success" color="#2FB801" />;
                } else if (row.evaluation === DynamicLeverEvaluation.FAVORABLE) {
                    return <SvgIcon size={14} name="error" color="#808080" />;
                } else {
                    return <SvgIcon size={14} name="error" color="#FD3559" />;
                }
            },
        },
        {
            title: "Evaluation",
            dataIndex: "evaluation",
            key: "evaluation",
            width: 120,
            render: (value: DynamicLeverEvaluation) => <EvaluationTag value={value} />,
        },

        {
            title: "Current state",
            dataIndex: "currentState",
            key: "currentState",
            width: 250,
        },
    ];

    const optimizableColumns: ColumnType<OrderFormDynamicLever>[] = [
        {
            title: "Priority",
            dataIndex: "priority",
            key: "priority",
            align: "center" as const,
            width: 90,
            className: classes.priority,
            render: (value: number) => <span className={classes[`priority${value}`]}></span>,
        },
        {
            title: "Lever",
            dataIndex: "name",
            key: "name",
            width: 220,
        },
        ...generalColumns,
        {
            title: "Optimal state",
            dataIndex: "optimalState",
            key: "optimalState",
            width: 250,
        },
        {
            title: "Justification",
            dataIndex: "justification",
            key: "justification",
            width: 300,
            className: classes.justificationCol,
            render: (value: string, row: OrderFormDynamicLever) => {
                if (!value) return null;

                const expanded = expandedRowKeys.includes(row.id);
                return (
                    <div className={clsx(classes.justificationCell, expanded && classes.expanded)}>
                        <Typography.Text ellipsis className={classes.justificationContent}>
                            {value}
                        </Typography.Text>
                        <Button
                            className={classes.justificationButton}
                            type="primary"
                            size="small"
                            icon={<SvgIcon name="glasses" />}
                            onClick={() => {
                                setExpandedRowKeys(expanded ? [] : [row.id]);
                            }}
                        ></Button>
                    </div>
                );
            },
        },
    ];

    const optimizedColumns: ColumnType<OrderFormDynamicLever>[] = [
        {
            title: "Lever",
            dataIndex: "name",
            key: "name",
            width: 310,
        },
        ...generalColumns,
        {
            title: "Optimal state",
            key: "optimalState",
            width: 250,
            render: () => {
                return "This lever is in its optimal state.";
            },
        },
        {
            title: "Justification",
            key: "justification",
            width: 300,
            render: () => {
                return <Tag color="success">Good standing</Tag>;
            },
        },
    ];

    const optimizableContent = (
        <>
            <Row align="middle" className={classes.header}>
                <Col flex={1}>
                    <div className={classes.title}>Levers requiring attention</div>
                    <div className={classes.description}>
                        These levers impact your projected savings under the current contract.
                    </div>
                </Col>
                <Col>
                    <Space size={24}>
                        <span className={classes.priority1}>1st priority</span>
                        <span className={classes.priority2}>2nd priority</span>
                        <span className={classes.priority3}>3rd priority</span>
                    </Space>
                </Col>
            </Row>
            {optimizable.length ? (
                <GenericTable
                    size="small"
                    rowKey="id"
                    className={classes.optimizableTable}
                    dataSource={optimizable}
                    pagination={false}
                    columns={optimizableColumns}
                    expandable={{
                        expandIcon: () => null,
                        showExpandColumn: false,
                        expandedRowKeys,
                        expandedRowClassName: () => classes.expandedRow,
                        expandedRowRender: (row: OrderFormDynamicLever) => {
                            return (
                                <>
                                    <div className={classes.expandedRowTitle}>Justification</div>
                                    <div className={classes.expandedRowContent}>
                                        {row.justification}
                                    </div>
                                </>
                            );
                        },
                    }}
                />
            ) : (
                <GenericCard size="large">
                    <div className={classes.checkmark}>
                        <SvgIcon name="success" size={32} color="#2FB801" />
                    </div>
                    <div className={classes.message}>
                        Your levers are good and require no attention.
                    </div>
                </GenericCard>
            )}
        </>
    );

    const optimizedContent = (
        <>
            <div className={classes.header}>
                <div className={classes.title}>Levers in good standing</div>
                <div className={classes.description}>
                    These levers are in good standing and do not require your attention.
                </div>
            </div>
            <GenericTable
                size="small"
                dataSource={optimized}
                columns={optimizedColumns}
                pagination={false}
                className={classes.optimizedTable}
            />
        </>
    );

    return (
        <div className={classes.container} style={style}>
            {optimized.length > 0 ? (
                <GenericTabs
                    activeKey={tab}
                    onChange={(k) => {
                        setTab(k as TabKey);
                    }}
                    items={[
                        {
                            key: TabKey.Optimizable,
                            label: TabKey.Optimizable,
                            children: optimizableContent,
                        },
                        {
                            key: TabKey.Optimized,
                            label: TabKey.Optimized,
                            children: optimizedContent,
                        },
                    ]}
                />
            ) : (
                optimizableContent
            )}
        </div>
    );
};
